import { useState, useEffect } from "react";
import { CalendarDays } from "lucide-react";
import Dropdown from "@/components/Dropdown";
import Checkbox from "@/components/Checkbox";
import { CheckboxVariant, Size } from "@ever-tutor/et-ui";
import type {
  FilterConfig,
  AppliedFilters,
  DateRange,
} from "../../constants/config/filter";
import { resolveOptionLabels } from "../../constants/config/filter";
import { formatDateRangeLabel } from "../../service/dateRange";
import type { AnalyticsData } from "../../types/data";
import { Key } from "../../constants/enums";

interface FilterDropdownProps {
  config: FilterConfig;
  data: AnalyticsData[];
  value: AppliedFilters[keyof AppliedFilters] | undefined;
  onChange: (value: AppliedFilters[keyof AppliedFilters] | undefined) => void;
  isOpen: boolean;
  onToggle: () => void;
  includeNotStarted?: boolean;
  nameHint?: string;
  onChangeIncludeNotStarted?: (value: boolean | undefined) => void;
  // Optional: batch-apply date range + includeNotStarted in one update
  onApplyBoth?: (
    value: AppliedFilters[keyof AppliedFilters] | undefined,
    includeNotStarted: boolean | undefined,
  ) => void;
}

const FilterDropdown = ({
  config,
  data,
  value,
  onChange,
  isOpen,
  onToggle,
  includeNotStarted,
  nameHint,
  onChangeIncludeNotStarted,
  onApplyBoth,
}: FilterDropdownProps) => {
  const [pendingValue, setPendingValue] = useState<
    AppliedFilters[keyof AppliedFilters] | undefined
  >(value);
  const [pendingIncludeNotStarted, setPendingIncludeNotStarted] = useState<
    boolean | undefined
  >(includeNotStarted);

  useEffect(() => {
    if (!isOpen) {
      setPendingValue(value);
      setPendingIncludeNotStarted(includeNotStarted);
    }
  }, [isOpen, value, includeNotStarted]);

  const handleToggle = () => {
    if (!isOpen) {
      // When opening, sync both value and includeNotStarted from props
      setPendingValue(value);
      setPendingIncludeNotStarted(includeNotStarted);
    }
    onToggle();
  };

  const handleApply = () => {
    // For Last Learned, prefer a batched apply to avoid overwriting
    if (config.key === Key.LAST_LEARNED && onApplyBoth) {
      onApplyBoth(
        pendingValue,
        pendingIncludeNotStarted === undefined
          ? true
          : pendingIncludeNotStarted,
      );
    } else {
      onChange(pendingValue);
      if (config.key === Key.LAST_LEARNED && onChangeIncludeNotStarted) {
        onChangeIncludeNotStarted(
          pendingIncludeNotStarted === undefined
            ? true
            : pendingIncludeNotStarted,
        );
      }
    }
    onToggle();
  };

  const getTitle = (config: FilterConfig) => {
    if (!value) {
      if (config.key === Key.LAST_LEARNED) return "All time";
      return config.label;
    }

    if (config.key === Key.LAST_LEARNED) {
      return formatDateRangeLabel(value as DateRange);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return config.label;
      }
      if (value.length === 1) {
        return config.extractLabel
          ? resolveOptionLabels(data, config, value, nameHint)[0]
          : value[0];
      }
      return config.formatMultipleTitle
        ? config.formatMultipleTitle(value.length)
        : `${value.length} ${config.label}`;
    }

    return config.label;
  };

  return (
    <Dropdown
      title={config.key === Key.GOALS ? "Completion" : getTitle(config)}
      count={
        config.key === Key.GOALS && Array.isArray(value) && value.length > 0
          ? value.length
          : undefined
      }
      leadingIcon={
        config.key === Key.LAST_LEARNED ? (
          <CalendarDays size={15} aria-hidden="true" />
        ) : undefined
      }
      isOpen={isOpen}
      onToggle={handleToggle}
      variant="analytics"
      size={Size.SM}
      showActive={!!(value && Array.isArray(value) ? value.length > 0 : value)}
    >
      <div className="flex flex-col gap-3">
        {config.render({
          data,
          value: pendingValue,
          onChange: setPendingValue,
          onApply: handleApply,
          onClear: () => {
            setPendingValue(undefined);
            // Remove the applied filter directly, without committing unrelated
            // pending changes to the include-not-started checkbox.
            if (config.key === Key.LAST_LEARNED && onApplyBoth) {
              onApplyBoth(undefined, includeNotStarted ?? true);
            } else {
              onChange(undefined);
            }
            onToggle();
          },
          nameHint,
          committedValue: value,
          beforeActions:
            config.key === Key.LAST_LEARNED ? (
              <div className="p-2">
                <Checkbox
                  checked={pendingIncludeNotStarted ?? true}
                  onChange={(checked) => setPendingIncludeNotStarted(checked)}
                  variant={CheckboxVariant.SIMPLE}
                  labelClassName="text-sm font-mulish text-[#252525]"
                >
                  Include Not Started
                </Checkbox>
              </div>
            ) : undefined,
        })}
      </div>
    </Dropdown>
  );
};

export default FilterDropdown;
